import React, { useState } from 'react';
import api from '../../utils/api';
import { format } from 'date-fns';


const TemplateCard = ({ template, onEdit, onDelete, onPause, onResume }) => {
  const [expanded, setExpanded] = useState(false);
  const [generatedTasks, setGeneratedTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  
  const loadGeneratedTasks = async () => {
    setLoadingTasks(true);
    try {
      const response = await api.get(
        `/recurring-templates/${template.id}/generated-tasks?limit=10`
      );
      setGeneratedTasks(response.data);
    } catch (error) {
      console.error('Failed to load generated tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };
  
  const toggleExpanded = () => {
    if (!expanded && generatedTasks.length === 0) {
      loadGeneratedTasks();
    }
    setExpanded(!expanded);
  };
  
  const getFrequencyDisplay = () => {
    const rule = template.recurring_rule;
    if (rule.frequency === 'daily') {
      if (rule.daysOfWeek && rule.daysOfWeek.length > 0) {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const selectedDays = rule.daysOfWeek.map(d => days[d]).join(', ');
        return `Daily: ${selectedDays}`;
      }
      if (rule.interval > 1) {
        return `Every ${rule.interval} days`;
      }
      return 'Daily';
    }
    if (rule.frequency === 'weekly') {
      return rule.interval > 1 ? `Every ${rule.interval} weeks` : 'Weekly';
    }
    if (rule.frequency === 'monthly') {
      return rule.interval > 1 ? `Every ${rule.interval} months` : 'Monthly';
    }
    if (rule.frequency === 'yearly') {
      return 'Yearly';
    }
    return rule.frequency.charAt(0).toUpperCase() + rule.frequency.slice(1);
  };
  
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'Not set';
    try {
      const date = new Date(dateTimeString);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch {
      return dateTimeString;
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };
  
  const getRelativeTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      const now = new Date();
      const diffMs = date - now;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      
      if (diffDays < 0) return '(overdue)';
      if (diffDays === 0) {
        if (diffHours <= 0) return '(now)';
        if (diffHours === 1) return '(in 1 hour)';
        return `(in ${diffHours} hours)`;
      }
      if (diffDays === 1) return '(tomorrow)';
      if (diffDays < 7) return `(in ${diffDays} days)`;
      return '';
    } catch {
      return '';
    }
  };
  
  return (
    <div className={`border rounded-lg p-5 transition-all ${
      template.is_paused 
        ? 'bg-gray-50 border-gray-300' 
        : 'bg-white border-blue-200 shadow-sm hover:shadow-md'
    }`}>
      {/* Header Row */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Template Name */}
          <div className="flex items-center gap-3 mb-2">
            <div className="text-3xl">🔄</div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {template.name}
              </h3>
              {template.project_name && (
                <p className="text-sm text-gray-500">
                  {template.client_name && `${template.client_name} • `}
                  {template.project_name}
                </p>
              )}
            </div>
            {template.is_paused && (
              <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded font-medium">
                PAUSED
              </span>
            )}
          </div>
          
          {/* Description */}
          {template.description && (
            <p className="text-sm text-gray-600 mb-4 ml-12">
              {template.description}
            </p>
          )}
          
          {/* Template Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm ml-12">
            {/* Frequency */}
            <div>
              <div className="text-gray-500 text-xs mb-1 font-medium">Frequency</div>
              <div className="font-medium text-gray-900">{getFrequencyDisplay()}</div>
            </div>
            
            {/* Due Time */}
            {template.recurring_due_time && (
              <div>
                <div className="text-gray-500 text-xs mb-1 font-medium">Due Time</div>
                <div className="font-medium text-gray-900">
                  {template.recurring_due_time.substring(0, 5)}
                </div>
              </div>
            )}
            
            {/* Assignees */}
            <div>
              <div className="text-gray-500 text-xs mb-1 font-medium">Assignees</div>
              <div className="font-medium text-gray-900">
                {template.assignee_names && template.assignee_names.length > 0 ? (
                  <>
                    {template.assignee_names.slice(0, 2).join(', ')}
                    {template.assignee_names.length > 2 && ` +${template.assignee_names.length - 2}`}
                  </>
                ) : (
                  <span className="text-gray-400">None</span>
                )}
              </div>
            </div>
            
            {/* Priority */}
            <div>
              <div className="text-gray-500 text-xs mb-1 font-medium">Priority</div>
              <div className={`font-medium ${
                template.priority === 'Critical' ? 'text-red-600' :
                template.priority === 'High' ? 'text-orange-600' :
                template.priority === 'Low' ? 'text-green-600' :
                'text-gray-900'
              }`}>
                {template.priority || 'Normal'}
              </div>
            </div>
          </div>
          
          {/* Next Generation - Prominent Display */}
          <div className="mt-4 ml-12 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-blue-600 font-semibold">Next Generation:</span>
              <span className="font-medium text-gray-900">
                {template.next_generation_datetime 
                  ? formatDateTime(template.next_generation_datetime)
                  : formatDate(template.next_generation_date) || 'Not scheduled'
                }
              </span>
              <span className="text-blue-600 font-medium">
                {template.next_generation_datetime && getRelativeTime(template.next_generation_datetime)}
              </span>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex gap-6 mt-3 ml-12 text-sm text-gray-600">
            <div>
              <span className="font-semibold text-gray-900">{template.stats.total_generated}</span> tasks generated
            </div>
            <div>
              <span className="font-semibold text-gray-900">{template.stats.generated_this_week}</span> this week
            </div>
            <div>
              Last: {template.last_generated_at 
                ? formatDate(template.last_generated_at)
                : 'Never'
              }
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-col gap-2 ml-4">
          <button
            onClick={onEdit}
            className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors flex items-center gap-1"
            title="Edit Template"
          >
            <span>✏️</span> Edit
          </button>
          
          {template.is_paused ? (
            <button
              onClick={onResume}
              className="px-3 py-1.5 text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded transition-colors flex items-center gap-1"
              title="Resume Generation"
            >
              <span>▶️</span> Resume
            </button>
          ) : (
            <button
              onClick={onPause}
              className="px-3 py-1.5 text-sm bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded transition-colors flex items-center gap-1"
              title="Pause Generation"
            >
              <span>⏸️</span> Pause
            </button>
          )}
          
          <button
            onClick={onDelete}
            className="px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors"
            title="Delete Template"
          >
            🗑️
          </button>
          
          <button
            onClick={toggleExpanded}
            className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            title="Show Generated Tasks"
          >
            {expanded ? '▲' : '▼'}
          </button>
        </div>
      </div>
      
      {/* Expanded Section: Generated Tasks */}
      {expanded && (
        <div className="mt-4 pt-4 border-t ml-12">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">
            Recently Generated Tasks (Last 10)
          </h4>
          
          {loadingTasks ? (
            <div className="text-center py-4 text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-xs">Loading...</p>
            </div>
          ) : generatedTasks.length === 0 ? (
            <div className="text-center py-6 text-gray-500 bg-gray-50 rounded">
              No tasks generated yet
            </div>
          ) : (
            <div className="space-y-2">
              {generatedTasks.map(task => (
                <div 
                  key={task.id} 
                  className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded text-sm transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      task.status === 'Complete' ? 'bg-green-100 text-green-700' :
                      task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                      task.status === 'Blocked' ? 'bg-red-100 text-red-700' :
                      'bg-gray-200 text-gray-700'
                    }`}>
                      {task.status}
                    </span>
                    <span className="font-medium text-gray-900">{task.name}</span>
                    {task.due_date && (
                      <span className="text-gray-500 text-xs">
                        Due: {formatDate(task.due_date)}
                      </span>
                    )}
                  </div>
                  <div className="text-gray-500 text-xs">
                    Created: {formatDate(task.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateCard;
